import type { Point } from '../state/types';
import { pointToLineDistance } from '../geometry/pointDistance';

export interface ConnectorGeometryInput {
  startPoint: Point;
  endPoint: Point;
  waypoints: Point[];
  arcPoint: Point | null;
}

export interface ArrowPoints {
  from: Point;
  to: Point;
}

export const getConnectorPoints = (input: ConnectorGeometryInput): Point[] => [
  input.startPoint,
  ...input.waypoints,
  input.endPoint,
];

export const getConnectorPathData = (input: ConnectorGeometryInput): string => {
  if (input.arcPoint && input.waypoints.length === 0) {
    return `M ${input.startPoint.x} ${input.startPoint.y} Q ${input.arcPoint.x} ${input.arcPoint.y} ${input.endPoint.x} ${input.endPoint.y}`;
  }

  const points = getConnectorPoints(input);
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
};

export const getConnectorTextPosition = (input: ConnectorGeometryInput): Point => {
  if (input.arcPoint && input.waypoints.length === 0) {
    return {
      x: 0.25 * input.startPoint.x + 0.5 * input.arcPoint.x + 0.25 * input.endPoint.x,
      y: 0.25 * input.startPoint.y + 0.5 * input.arcPoint.y + 0.25 * input.endPoint.y,
    };
  }

  const points = getConnectorPoints(input);
  const midIndex = Math.floor(points.length / 2);
  const p1 = points[midIndex - 1] ?? points[0]!;
  const p2 = points[midIndex] ?? points[0]!;
  return {
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2,
  };
};

export const getConnectorEndArrowPoints = (input: ConnectorGeometryInput): ArrowPoints => {
  if (input.arcPoint && input.waypoints.length === 0) {
    return { from: input.arcPoint, to: input.endPoint };
  }

  const points = getConnectorPoints(input);
  const to = points[points.length - 1] ?? input.endPoint;
  const from = points[points.length - 2] ?? input.startPoint;
  return { from, to };
};

export const getConnectorStartArrowPoints = (input: ConnectorGeometryInput): ArrowPoints => {
  if (input.arcPoint && input.waypoints.length === 0) {
    return { from: input.arcPoint, to: input.startPoint };
  }

  const points = getConnectorPoints(input);
  const to = points[0] ?? input.startPoint;
  const from = points[1] ?? input.endPoint;
  return { from, to };
};

export const pointToSegmentDistance = (p: Point, a: Point, b: Point): number => {
  return pointToLineDistance(p, a, b, true);
};

const getBezierPoint = (start: Point, control: Point, end: Point, t: number): Point => {
  const oneMinusT = 1 - t;
  return {
    x: oneMinusT * oneMinusT * start.x + 2 * oneMinusT * t * control.x + t * t * end.x,
    y: oneMinusT * oneMinusT * start.y + 2 * oneMinusT * t * control.y + t * t * end.y,
  };
};

export const pointToBezierDistance = (
  p: Point,
  start: Point,
  control: Point,
  end: Point
): number => {
  const curveLength =
    Math.hypot(end.x - start.x, end.y - start.y) +
    Math.hypot(control.x - start.x, control.y - start.y) +
    Math.hypot(end.x - control.x, end.y - control.y);
  const samples = Math.max(50, Math.ceil(curveLength / 5));

  let minDist = Infinity;

  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    const point = getBezierPoint(start, control, end, t);
    const dist = Math.hypot(p.x - point.x, p.y - point.y);
    minDist = Math.min(minDist, dist);
  }

  return minDist;
};
